import { UnknownObject } from '@/shared/types/utility-types';
import { isEnumValue, isFieldsInObject } from '@/shared/utils/guards';
import { log } from '@/shared/utils/log';

import { CollectionName } from '../../../constants';
import { AssetCategory } from '../../../models/asset.dto';
import { singleCollectionMigration } from '../../helpers';
import {
  CurrentMigrationCollectionItem,
  MigratedMigrationCollectionItem,
  MigratedMigrationCollectionsData,
  MigrationType,
  SingleCollectionMigrationOption,
} from '../../types';

import { getFieldsToFillHelper, requiredFieldsCheck } from './requiredFieldsCheck';

const getAssetWithoutFields = <
  T extends AssetWithCategory,
  TKey extends Exclude<string, 'category'>,
>(
  data: T,
  fields: TKey[],
): AssetWithCategory => {
  if (!isFieldsInObject(data, fields)) {
    return data;
  }

  const result = structuredClone(data);
  for (const field of fields) {
    delete result[field];
  }

  return result;
};

type AssetWithCategory = UnknownObject & { category: AssetCategory };
const migrateRenamedFields = (data: AssetWithCategory): AssetWithCategory => {
  switch (data.category) {
    case AssetCategory.BONDS: {
      if (!isFieldsInObject(data, ['keyAssetsQuantity'])) {
        return data;
      }

      const { keyAssetsQuantity, ...newData } = data;

      return { ...newData, amount: keyAssetsQuantity };
    }
    case AssetCategory.SECURITIES_AND_STOCKS: {
      if (!isFieldsInObject(data, ['quantity'])) {
        return data;
      }

      const { quantity, ...newData } = data;

      return { ...newData, amount: quantity };
    }
    case AssetCategory.JEWELLERY: {
      if (!isFieldsInObject(data, ['receipt', 'attachments'])) {
        return data;
      }

      const { receipt, attachments, ...rest } = data;

      if (!receipt || typeof receipt !== 'string') {
        return data;
      }

      if (Array.isArray(attachments)) {
        return { ...rest, attachments: attachments.concat(receipt) };
      }

      return { ...rest, attachments: [receipt] };
    }
    case AssetCategory.PRECIOUS_STONES: {
      if (!isFieldsInObject(data, ['countryOfAcquisition', 'country'])) {
        return data;
      }

      const { countryOfAcquisition, country, ...rest } = data;

      return { ...rest, country: country || countryOfAcquisition };
    }

    default:
      return data;
  }
};

const migrateDeletedFields = (data: AssetWithCategory): AssetWithCategory => {
  switch (data.category) {
    case AssetCategory.COMMERCIAL_REAL_ESTATE:
    case AssetCategory.RESIDENTIAL_REAL_ESTATE: {
      return getAssetWithoutFields(data, ['landPlotSquare']);
    }
    case AssetCategory.BROKERAGE_ACCOUNT: {
      return getAssetWithoutFields(data, ['totalBalance']);
    }
    case AssetCategory.SECURITIES_AND_STOCKS: {
      return getAssetWithoutFields(data, ['keyAssets', 'hasInsurance']);
    }
    case AssetCategory.BONDS: {
      return getAssetWithoutFields(data, ['keyAssets', 'insurance', 'bondsType']);
    }

    default: {
      return data;
    }
  }
};

const handleMigrationsList = ({
  data,
  migrations,
}: {
  migrations: ((data: AssetWithCategory) => AssetWithCategory)[];
  data: AssetWithCategory;
}) => {
  const migratedData = migrations.reduce<AssetWithCategory>(
    (previousData, migration) => migration(previousData),
    data,
  );

  return migratedData;
};

const migrateAsset = (data: CurrentMigrationCollectionItem): MigratedMigrationCollectionItem => {
  if (!isFieldsInObject(data, ['category'])) {
    return data as MigratedMigrationCollectionItem;
  }

  if (!isEnumValue(data.category, AssetCategory)) {
    return data as MigratedMigrationCollectionItem;
  }

  const assetWithCategory = data as AssetWithCategory;

  const migratedData = handleMigrationsList({
    data: assetWithCategory,
    migrations: [migrateRenamedFields, migrateDeletedFields],
  });

  return migratedData;
};

const ASSET_REQ_FIELDS_CONFIG: Partial<Record<AssetCategory, string[]>> = {
  [AssetCategory.DEBIT_ACCOUNTS]: ['type'],
  [AssetCategory.RESIDENTIAL_REAL_ESTATE]: ['type'],
  [AssetCategory.COMMERCIAL_REAL_ESTATE]: ['type'],
  [AssetCategory.JEWELLERY]: ['type', 'storageLocation'],
  [AssetCategory.BANK_CELLS]: ['type', 'accessMethod'],
  [AssetCategory.METAL_ACCOUNTS]: ['type'],
  [AssetCategory.CENTRALIZED_WALLET]: ['holder'],
  [AssetCategory.DECENTRALIZED_WALLET]: ['mnemonicStorageLocation'],
  [AssetCategory.PRECIOUS_STONES]: ['type', 'weight', 'storageLocation', 'typeOfOrigin'],
  [AssetCategory.COMMODITY_ASSET_TOKENS]: ['amount'],
  [AssetCategory.BROKERAGE_ACCOUNT]: ['type'],
  [AssetCategory.SECURITIES_AND_STOCKS]: ['type', 'amount'],
  [AssetCategory.BONDS]: ['type', 'amount'],
  [AssetCategory.EXCHANGE_OPTIONS]: ['type', 'amount'],
  [AssetCategory.OVER_THE_COUNTER_OPTIONS]: ['type', 'amount'],
  [AssetCategory.FUTURES]: ['type', 'amount'],
  [AssetCategory.FORWARDS]: ['type', 'amount'],
  [AssetCategory.MY_FUND]: ['type'],
  [AssetCategory.PUBLIC_FUND]: ['type', 'fullLegalName'],
};

const assetFieldsCheck = (asset: MigratedMigrationCollectionItem): string[] | undefined => {
  if (!isFieldsInObject(asset, ['category'])) {
    return undefined;
  }

  if (!isEnumValue(asset.category, AssetCategory)) {
    return undefined;
  }

  const category = asset.category;

  const requiredFields = ASSET_REQ_FIELDS_CONFIG[category];

  if (!requiredFields) {
    return undefined;
  }

  const fieldsToFill = getFieldsToFillHelper({ item: asset, requiredFields });

  return fieldsToFill;
};

const MIGRATION_OPTIONS: SingleCollectionMigrationOption[] = [
  { collectionName: CollectionName.ASSETS, migrateItemFunction: migrateAsset },
];

const migrate: MigrationType['migrate'] = async (
  data,
  { hookUpdateCollections: onUserActionRequired },
) => {
  const migratedData: MigratedMigrationCollectionsData = new Map();

  MIGRATION_OPTIONS.forEach(({ collectionName, migrateItemFunction }) => {
    const collection = data.get(collectionName);

    if (!collection) {
      log.error(`[MIGRATION ERROR]: ${collectionName} not found`);
      return;
    }

    const migratedCollection = singleCollectionMigration({ collection, migrateItemFunction });
    migratedData.set(collectionName, migratedCollection);
  });

  const migratedWithRequired = await requiredFieldsCheck({
    config: { assets: assetFieldsCheck },
    onUserActionRequired,
    migratedData,
  });

  return migratedWithRequired;
};

export const migration4: MigrationType = {
  migrate,
  version: 5,
};
